import React from 'react';
import { Modal, Input, FormPanel, SubmitButton, RadioButtonGroup } from '@ixicare/ui';
import { Device, DeviceEntryProps, DeviceExistBody, deviceTypesWithOwner } from '@ixicare/resources';
import { t } from '~/i18n';
import { ArchivedDeviceInfo } from './DeviceCreateModal';

interface DeviceSearchModalProps {
  onClose: () => void;
  onDeviceFound: (device: DeviceEntryProps) => void;
  onArchivedDeviceFound: (info: ArchivedDeviceInfo) => void;
}

type SearchType = 'mac' | 'imei';

export const DeviceSearchModal: React.FC<DeviceSearchModalProps> = ({
  onClose,
  onDeviceFound,
  onArchivedDeviceFound,
}) => {
  const [searchType, setSearchType] = React.useState<SearchType>('mac');
  const [searchValue, setSearchValue] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const searchOptions = [
    { value: 'mac', label: t('deviceDetail.macAddress') },
    { value: 'imei', label: t('devices.table.imeiNumber') },
  ];

  const handleSubmit = async (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!searchValue.trim()) return;

    setLoading(true);
    setError(null);

    try {
      const existResult: DeviceExistBody =
        searchType === 'mac'
          ? await Device.deviceMacAddressExist(searchValue.trim())
          : await Device.deviceImeiNumberExist(searchValue.trim());

      // The exists check is global now (a 404 means "no such device"). A device
      // on a site outside the caller's scope must be rejected up front — before
      // the archived/type branches — since fetching it would 403.
      if (existResult.inAllowedSites === false) {
        setError(t('device.form.infobox.otherSite'));
        return;
      }

      if (existResult.isArchived) {
        if (!existResult.type) {
          setError(t('deviceSearch.error.notFound'));
          return;
        }
        onArchivedDeviceFound({
          deviceType: existResult.type,
          macAddress: searchType === 'mac' ? searchValue.trim() : undefined,
          imeiNumber: searchType === 'imei' ? searchValue.trim() : undefined,
          siteId: existResult.siteId,
        });
        return;
      }

      if (existResult.type && !deviceTypesWithOwner.includes(existResult.type)) {
        setError(t('scan.notLinkable.description'));
        return;
      }

      const device = await Device.getDevice(existResult.id, true);

      if (!device.deviceType || !deviceTypesWithOwner.includes(device.deviceType)) {
        setError(t('scan.notLinkable.description'));
        return;
      }

      onDeviceFound(device);
    } catch {
      setError(t('deviceSearch.error.notFound'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal onClose={onClose} style={{ maxWidth: 450 }}>
      <h3 className="text-center mb-30">{t('deviceSearch.title')}</h3>
      <FormPanel>
        <form onSubmit={handleSubmit}>
          <div className="mb-30">
            <RadioButtonGroup
              options={searchOptions}
              onChange={(v: SearchType) => {
                setSearchType(v);
                setError(null);
              }}
              value={searchType}
              name="search-type"
            />
          </div>
          <Input
            label={searchType === 'mac' ? t('devices.table.macAddress') : t('devices.table.imeiNumber')}
            value={searchValue}
            onValueChange={setSearchValue}
            error={error ?? undefined}
            autoFocus
          />
          <div className="d-flex justify-content-end mt-30">
            <SubmitButton type="submit" loading={loading}>
              {t('profiles.filters.search')}
            </SubmitButton>
          </div>
        </form>
      </FormPanel>
    </Modal>
  );
};
